import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  getDocs,
  writeBatch,
  arrayUnion,
  Timestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useCompany } from './CompanyContext';
import { sanitizeForFirestore } from '../utils/firestoreUtils';
import { normalizeTaskWrite } from '../utils/jobMode';

const TasksContext = createContext();

/** Valid reschedule reasons. Keep this enum tight — the Activity Timeline
 * + future reporting will pivot on it. */
export const VALID_RESCHEDULE_REASONS = new Set([
  'customer_request',
  'weather',
  'technician',
  'office',
  'system',
]);

// ── Task window defaults ────────────────────────────────────────────────────
const TASK_WINDOW_PAST_DAYS = 30;
const TASK_WINDOW_FUTURE_DAYS = 90;

const isoDateOffset = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10); // YYYY-MM-DD
};

// Normalize a Date | ISO string into a window bound (string-comparable
// against ISO scheduledStart values).
const toWindowBound = (v, isEnd = false) => {
  if (!v) return null;
  const dateStr = v instanceof Date ? v.toISOString().slice(0, 10) : String(v).slice(0, 10);
  return isEnd ? `${dateStr}T23:59:59` : dateStr;
};

// Normalize a Firestore task doc → app task shape (legacy field mapping).
const normalizeTaskDoc = (taskDoc) => {
  const data = taskDoc.data();
  const resolvedType =
    data.type ||
    (data.taskCategory === 'job' ? 'job' : 'task');

  // Normalize job status: Firestore 'pending' → domain 'scheduled'
  let resolvedStatus = data.status || 'pending';
  if (resolvedType === 'job' && resolvedStatus === 'pending') {
    resolvedStatus = 'scheduled';
  }

  return {
    id: taskDoc.id,
    type: resolvedType,             // canonical field
    taskCategory: resolvedType,     // backward-compat alias (read-only)
    scheduledStart: data.scheduledStart || null,
    scheduledEnd: data.scheduledEnd || null,
    assignedTo: data.assignedTo || null,
    ...data,
    // Overwrite any raw values with the normalized ones
    status: resolvedStatus,
  };
};

export const useTasks = () => {
  const context = useContext(TasksContext);
  if (!context) {
    throw new Error('useTasks must be used within a TasksProvider');
  }
  return context;
};

export const TasksProvider = ({ children }) => {
  const { companyId } = useCompany();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Bounded task window ─────────────────────────────────────────────────────
  // Tasks are streamed for a movable scheduledStart window (default −30/+90
  // days) plus all unscheduled tasks (scheduledStart == null: admin todos and
  // needs-scheduling jobs). Calendar/Dispatch call ensureTaskWindow() when
  // navigating outside the window — real-time updates are preserved for the
  // visible operational range without loading lifetime history.
  const [taskWindow, setTaskWindow] = useState(() => ({
    start: isoDateOffset(-TASK_WINDOW_PAST_DAYS),
    end: `${isoDateOffset(TASK_WINDOW_FUTURE_DAYS)}T23:59:59`,
  }));

  const ensureTaskWindow = useCallback((start, end) => {
    const s = toWindowBound(start);
    const e = toWindowBound(end, true);
    setTaskWindow((w) => {
      const ns = s && s < w.start ? s : w.start;
      const ne = e && e > w.end ? e : w.end;
      if (ns === w.start && ne === w.end) return w;
      return { start: ns, end: ne };
    });
  }, []);

  // Per-lead full-history subscriptions (Customer Profile) — merged into
  // `tasks` so profile surfaces see the lead's complete job history even
  // outside the date window. Capped to the 10 most recently opened leads.
  const [leadSubs, setLeadSubs] = useState([]);
  const ensureLeadTasks = useCallback((leadId) => {
    if (!leadId) return;
    setLeadSubs((prev) => {
      if (prev.includes(leadId)) return prev;
      return [...prev.slice(-9), leadId];
    });
  }, []);

  const bucketsRef = React.useRef({ windowed: new Map(), unscheduled: new Map(), leadMaps: new Map() });
  const readyRef = React.useRef({ windowed: false, unscheduled: false });

  const publish = useCallback(() => {
    const b = bucketsRef.current;
    if (!readyRef.current.windowed || !readyRef.current.unscheduled) return;
    const merged = new Map();
    for (const leadMap of b.leadMaps.values()) {
      for (const [k, v] of leadMap) merged.set(k, v);
    }
    for (const [k, v] of b.unscheduled) merged.set(k, v);
    for (const [k, v] of b.windowed) merged.set(k, v);
    const tasksData = [...merged.values()];

    // Sort by dueDate + dueTime for easier consumption
    tasksData.sort((a, b2) => {
      const aDate = a.dueDate || '';
      const bDate = b2.dueDate || '';
      if (aDate !== bDate) return aDate.localeCompare(bDate);
      const aTime = a.dueTime || '';
      const bTime = b2.dueTime || '';
      return aTime.localeCompare(bTime);
    });

    setTasks(tasksData);
    setLoading(false);
  }, []);

  // Subscribe to tasks in real time — merged listeners (window + unscheduled)
  useEffect(() => {
    if (!db || !companyId) {
      // Not ready yet; AppReadyGate will prevent this provider from mounting.
      return;
    }

    const tasksRef = collection(db, 'companies', companyId, 'tasks');
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[TasksContext] Subscribing to tasks window:', taskWindow);
    }

    const onError = (label) => (error) => {
      console.error(`Tasks snapshot error (${label}):`, error);
      readyRef.current[label] = true;
      publish();
    };

    const unsubWindow = onSnapshot(
      query(
        tasksRef,
        where('scheduledStart', '>=', taskWindow.start),
        where('scheduledStart', '<=', taskWindow.end),
      ),
      (snapshot) => {
        bucketsRef.current.windowed = new Map(snapshot.docs.map((d) => [d.id, normalizeTaskDoc(d)]));
        readyRef.current.windowed = true;
        publish();
      },
      onError('windowed'),
    );

    const unsubUnscheduled = onSnapshot(
      query(tasksRef, where('scheduledStart', '==', null)),
      (snapshot) => {
        bucketsRef.current.unscheduled = new Map(snapshot.docs.map((d) => [d.id, normalizeTaskDoc(d)]));
        readyRef.current.unscheduled = true;
        publish();
      },
      onError('unscheduled'),
    );

    return () => {
      unsubWindow();
      unsubUnscheduled();
    };
  }, [companyId, taskWindow, publish]);

  // Per-lead history listeners
  useEffect(() => {
    if (!db || !companyId || leadSubs.length === 0) return;

    const tasksRef = collection(db, 'companies', companyId, 'tasks');
    const unsubs = leadSubs.map((leadId) =>
      onSnapshot(
        query(tasksRef, where('leadId', '==', leadId)),
        (snapshot) => {
          bucketsRef.current.leadMaps.set(
            leadId,
            new Map(snapshot.docs.map((d) => [d.id, normalizeTaskDoc(d)])),
          );
          publish();
        },
        (error) => console.error(`Tasks snapshot error (lead ${leadId}):`, error),
      )
    );

    return () => {
      unsubs.forEach((u) => u());
      bucketsRef.current.leadMaps.clear();
    };
  }, [companyId, leadSubs, publish]);

  // Add new task
  const addTask = async (taskData) => {
    if (!db || !companyId) {
      throw new Error('Firestore is not initialized');
    }

    const authUser = auth.currentUser;

    // Enforce the two-mode invariant: appointment requires real time;
    // route-flexible strips synthetic hour/minute. Job-type tasks only —
    // admin tasks (type='task') keep their existing dueDate/dueTime shape.
    let _norm = taskData;
    if ((taskData?.type || (taskData?.taskCategory === 'job' ? 'job' : 'task')) === 'job') {
      try {
        _norm = { ...taskData, ...normalizeTaskWrite(taskData) };
      } catch (err) {
        // Caller violated the invariant (appointment without time). Surface
        // it loudly rather than silently writing a corrupt task.
        throw err;
      }
    }

    try {
      const tasksRef = collection(db, 'companies', companyId, 'tasks');
      const payload = sanitizeForFirestore({
        leadId: _norm.leadId,
        title: _norm.title,
        description: _norm.description || '',
        status: _norm.status || 'pending',
        priority: _norm.priority || 'medium',
        dueDate: _norm.dueDate || null,
        dueTime: _norm.dueTime || null,
        taskType: _norm.taskType || 'other',
        type: _norm.type || (_norm.taskCategory === 'job' ? 'job' : 'task'),
        // Billing Intent — stamp explicit intent ONLY on actual service/job
        // records (type==='job'). Admin tasks / reminders / internal records
        // stay untagged (legacy/unknown), never auto-billable. Callers may
        // pass billingIntent:'non_billable' + nonBillableReason for No Charge.
        ...(((_norm.type || (_norm.taskCategory === 'job' ? 'job' : 'task')) === 'job')
          ? {
              billingIntent: _norm.billingIntent || 'billable',
              ...(_norm.nonBillableReason ? { nonBillableReason: _norm.nonBillableReason } : {}),
              ...(_norm.nonBillableNote ? { nonBillableNote: _norm.nonBillableNote } : {}),
            }
          : {}),
        scheduledStart: _norm.scheduledStart || null,
        scheduledEnd: _norm.scheduledEnd || null,
        jobMode: _norm.jobMode || undefined,
        hasTime: typeof _norm.hasTime === 'boolean' ? _norm.hasTime : undefined,
        serviceDate: _norm.serviceDate || undefined,
        assignedTo: _norm.assignedTo || null,
        // Client identity fields — stored on the task so Dispatch/Calendar can show them
        // without requiring a secondary lead lookup to resolve the name.
        clientName: taskData.clientName || null,
        serviceType: taskData.serviceType || null,
        createdBy: taskData.createdBy || (authUser ? authUser.uid : null),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const docRef = await addDoc(tasksRef, payload);
      return docRef.id;
    } catch (error) {
      console.error('Error adding task:', error);
      throw error;
    }
  };

  // Complete a job — auto-generates final invoice if job has a linked quote.
  // Falls back to direct Firestore status update if backend is unavailable.
  /**
   * Sprint 4 (Single Completion Contract) — CANONICAL job-completion path.
   *
   * Architectural contract: any task where type === 'job' must complete
   * through this helper, which POSTs to /api/jobs/{id}/complete. The
   * backend is the single owner of:
   *   • status='completed' + completedAt
   *   • recurrence engine (_maybe_create_recurring_job)
   *   • next-service override consumption
   *   • final invoice / Stripe / auto-charge / SMS
   *
   * There is NO Firestore fallback. If the backend is unreachable, the
   * promise rejects so the caller can surface the error to the operator
   * — we would rather refuse a completion than silently bypass
   * recurrence and produce drift.
   *
   * @returns {Promise<{success: boolean, invoice: object|null, message?: string, ...}>}
   * @throws  {Error} when the backend cannot complete the transaction.
   */
  const completeJob = async (taskId, { quoteId, originUrl } = {}) => {
    if (!taskId) throw new Error('completeJob: taskId is required');
    if (!companyId) throw new Error('completeJob: companyId is not initialized');

    const token = await auth.currentUser?.getIdToken(false);
    if (!token) throw new Error('completeJob: not authenticated');

    // Allow the caller to omit quoteId — the backend will read it from
    // the task document itself if present, and gracefully no-op when
    // absent (returns invoice:null with success:true).
    const effectiveQuoteId = quoteId
      ?? tasks.find((t) => t.id === taskId)?.quoteId
      ?? null;

    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${taskId}/complete`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          companyId,
          quoteId:   effectiveQuoteId,
          originUrl: originUrl || window.location.origin,
        }),
      }
    );

    // 4xx / 5xx → throw, caller surfaces via toast. No silent local fallback.
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try {
        const errBody = await res.json();
        detail = errBody?.detail || errBody?.message || detail;
      } catch { /* ignore parse failures */ }
      throw new Error(`Job completion failed — ${detail}`);
    }

    const data = await res.json().catch(() => ({}));
    if (data?.success === false) {
      throw new Error(data?.message || 'Job completion failed');
    }
    return data;
  };

  // Update existing task
  const updateTask = async (taskId, updates) => {
    if (!db || !companyId) {
      throw new Error('Firestore is not initialized');
    }

    try {
      const taskRef = doc(db, 'companies', companyId, 'tasks', taskId);
      // If this update touches scheduling fields, run it through the
      // two-mode normalizer so route writes can't carry synthetic times and
      // appointment writes can't lose their time. Updates without jobMode +
      // scheduledStart bypass normalization (e.g. pure status/note changes).
      let _u = updates;
      const _touchesSchedule = updates && (
        'jobMode' in updates || 'scheduledStart' in updates || 'hasTime' in updates
      );
      if (_touchesSchedule) {
        try {
          _u = { ...updates, ...normalizeTaskWrite(updates) };
        } catch (err) {
          throw err;
        }
      }
      await updateDoc(
        taskRef,
        sanitizeForFirestore({
          ..._u,
          updatedAt: serverTimestamp(),
        })
      );
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  };

  // Delete task
  const deleteTask = async (taskId) => {
    if (!db || !companyId) {
      throw new Error('Firestore is not initialized');
    }

    try {
      const taskRef = doc(db, 'companies', companyId, 'tasks', taskId);
      await deleteDoc(taskRef);
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  };

  // Get tasks for a specific lead (helper)
  const getTasksForLead = (leadId) => {
    if (!leadId) return [];
    return tasks.filter((task) => task.leadId === leadId);
  };

  // Optional: delete all tasks for a lead (not wired to UI yet)
  const deleteTasksForLead = async (leadId) => {
    if (!db || !companyId) {
      throw new Error('Firestore is not initialized');
    }
    if (!leadId) return;

    try {
      const tasksRef = collection(db, 'companies', companyId, 'tasks');
      const q = query(tasksRef, where('leadId', '==', leadId));
      const snapshot = await getDocs(q);

      const { chunkedBatch } = await import('../lib/firestoreBatch');
      await chunkedBatch(db, snapshot.docs, (batch, docSnap) => batch.delete(docSnap.ref));
    } catch (error) {
      console.error('Error deleting tasks for lead:', error);
      throw error;
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Jobs vs Tasks — logical separation (iteration 146).
  //
  // One Firestore collection under the hood, two memoized virtual views:
  //   jobs        → type === 'job'   (field work, dispatchable, billable)
  //   adminTasks  → type !== 'job'   (callbacks, reminders, internal workflow)
  //
  // Surfaces should prefer these selectors over `tasks.filter(...)` so the
  // separation can never drift. The raw `tasks` array is still exposed for
  // mixed surfaces (LeadTasksPanel, audienceEngine, debug).
  //
  // Typed write helpers enforce the `type` field so a caller can never forget
  // it — which means a new playbook-generated reminder can never accidentally
  // leak onto /dispatch, /jobs, /today, /calendar.
  // ───────────────────────────────────────────────────────────────────────────
  const jobs       = useMemo(() => tasks.filter((t) => t.type === 'job'), [tasks]);
  const adminTasks = useMemo(() => tasks.filter((t) => t.type !== 'job'), [tasks]);

  // Typed write helpers — enforce `type` and prevent callers from mutating
  // it on update so a job can't become an admin task mid-update and leak onto
  // the wrong surface.
  const addJob         = (data) => addTask({ ...data, type: 'job' });
  const addAdminTask   = (data) => addTask({ ...data, type: 'task' });

  const updateJob       = (id, updates) => {
    const { type: _t, ...safe } = updates || {};
    return updateTask(id, safe);
  };
  const updateAdminTask = (id, updates) => {
    const { type: _t, ...safe } = updates || {};
    return updateTask(id, safe);
  };

  // completeJob (canonical) is the ONLY completion path — see ConfirmCompleteDialog.

  /**
   * Sprint 1 (Recurring Scheduling) — CANONICAL schedule-change write path.
   *
   * Architectural contract: the task is the single source of truth for any
   * visit that has been created. EVERY code path that changes a task's
   * schedule (Customer Profile reschedule, Calendar drag/drop, Dispatch
   * Reschedule modal, future Bulk Weather Reschedule endpoint, automation)
   * MUST go through this helper. It atomically:
   *
   *   1. updates scheduledStart / scheduledEnd / jobMode / hasTime on the task
   *   2. appends one bounded entry to tasks/{id}.history[] (capped at 20)
   *   3. for recurring tasks, syncs leads/{leadId}.recurrence.nextJobDate
   *      so the cached cursor stays coherent with the task (Activity Timeline
   *      derives the change event from this same history array).
   *
   * @param {string} taskId
   * @param {object} change
   * @param {Date|string} change.newScheduledStart       — required
   * @param {Date|string} [change.newScheduledEnd]
   * @param {string}      [change.newJobMode]            — 'appointment' | 'route_flexible'
   * @param {boolean}     [change.newHasTime]
   * @param {string}      change.reason                  — required: see VALID_RESCHEDULE_REASONS
   * @param {string}      [change.note]                  — optional, ≤ 280 chars
   */
  const rescheduleTask = async (taskId, change) => {
    if (!db || !companyId) {
      throw new Error('Firestore is not initialized');
    }
    if (!taskId) throw new Error('rescheduleTask: taskId is required');
    if (!change || change.newScheduledStart === undefined) {
      throw new Error('rescheduleTask: newScheduledStart is required (use null to unschedule)');
    }
    if (!change.reason || !VALID_RESCHEDULE_REASONS.has(change.reason)) {
      throw new Error(
        `rescheduleTask: reason must be one of ${[...VALID_RESCHEDULE_REASONS].join(', ')}`
      );
    }

    // Locate the existing task (we already have it from the live subscription).
    const existing = tasks.find((t) => t.id === taskId);
    if (!existing) {
      throw new Error(`rescheduleTask: task ${taskId} not found in live cache`);
    }

    const toIso = (v) => {
      if (v == null) return null;
      if (v instanceof Date) return v.toISOString();
      return typeof v === 'string' ? v : String(v);
    };

    const fromStart = existing.scheduledStart || null;
    const toStart   = toIso(change.newScheduledStart);
    const fromEnd   = existing.scheduledEnd || null;
    const toEnd     = change.newScheduledEnd !== undefined ? toIso(change.newScheduledEnd) : fromEnd;
    const isUnschedule = toStart == null;

    // No-op guard — if nothing changed, don't write history noise.
    if (fromStart === toStart && fromEnd === toEnd) return { changed: false };

    // ── Build the task update with two-mode normalization ─────────────────
    const rawTaskUpdate = {
      scheduledStart: toStart,
      scheduledEnd:   isUnschedule ? null : toEnd,
    };
    if (change.newJobMode != null) rawTaskUpdate.jobMode = change.newJobMode;
    if (change.newHasTime != null) rawTaskUpdate.hasTime = change.newHasTime;
    if (change.newStatus != null)  rawTaskUpdate.status  = change.newStatus;
    let normalized;
    try {
      // Skip jobMode/hasTime normalization on unschedule (no times to infer).
      normalized = isUnschedule
        ? rawTaskUpdate
        : { ...rawTaskUpdate, ...normalizeTaskWrite(rawTaskUpdate) };
    } catch (err) {
      throw err;
    }

    // ── History entry (bounded; cap on read-side at 20) ───────────────────
    const user = auth?.currentUser || null;
    const historyEntry = sanitizeForFirestore({
      type:       isUnschedule ? 'unscheduled' : 'rescheduled',
      at:         Timestamp.now(),          // serverTimestamp() forbidden inside arrayUnion
      by:         user?.uid || null,
      byName:     user?.displayName || user?.email || null,
      fromStart,
      toStart,
      fromEnd,
      toEnd:      isUnschedule ? null : toEnd,
      reason:     change.reason,
      note:       (change.note || '').slice(0, 280) || null,
    });

    // ── Recurring-cache mirror (Sprint 1 contract) ────────────────────────
    // Unschedule on a recurring lead clears the cursor so Service Plan /
    // Outlook don't show a date for a visit no longer on the calendar.
    let leadCacheUpdate = null;
    if (existing.leadId) {
      const leadSnap = await getDocs(query(
        collection(db, 'companies', companyId, 'leads'),
        where('__name__', '==', existing.leadId),
      )).catch(() => null);
      const leadDoc = leadSnap?.docs?.[0];
      if (leadDoc?.data()?.recurrence?.active === true) {
        leadCacheUpdate = {
          'recurrence.nextJobDate': isUnschedule ? null : toStart,
          updatedAt: serverTimestamp(),
        };
      }
    }

    // ── Atomic batch write ────────────────────────────────────────────────
    // Sprint 1 contract: history[] is bounded to 20 entries. We trim on the
    // WRITE side too (not just on the read side) so the Firestore document
    // can't grow unbounded for high-churn tasks.
    const HISTORY_CAP = 20;
    const existingHistory = Array.isArray(existing.history) ? existing.history : [];
    let historyWrite;
    if (existingHistory.length < HISTORY_CAP) {
      historyWrite = arrayUnion(historyEntry);
    } else {
      // Replace the array with the last (CAP - 1) entries + the new one.
      historyWrite = [...existingHistory.slice(-(HISTORY_CAP - 1)), historyEntry];
    }

    const batch = writeBatch(db);
    const taskRef = doc(db, 'companies', companyId, 'tasks', taskId);
    batch.update(taskRef, sanitizeForFirestore({
      ...normalized,
      history: historyWrite,
      updatedAt: serverTimestamp(),
    }));
    if (leadCacheUpdate && existing.leadId) {
      batch.update(
        doc(db, 'companies', companyId, 'leads', existing.leadId),
        leadCacheUpdate,
      );
    }
    await batch.commit();
    return { changed: true, recurringCursorSynced: leadCacheUpdate != null };
  };

  /**
   * Sprint 2 — bulk reschedule for Dispatch "Move Day…" / "Move Selected…".
   *
   * Reuses the canonical rescheduleTask helper one task at a time so every
   * touched task gets its own bounded history[] entry + recurrence cursor
   * sync. Preserves time-of-day, duration, jobMode, hasTime, assignedTo,
   * status by computing newScheduledStart/End from the existing task plus
   * the requested {targetDate} (a Date pointing to midnight local time of
   * the destination day).
   *
   * @param {string[]} taskIds          — tasks to move (job-type expected)
   * @param {object}   change
   * @param {Date}     change.targetDate — destination day (local midnight)
   * @param {string}   change.reason     — one of VALID_RESCHEDULE_REASONS
   * @param {string}  [change.note]      — optional, ≤ 280 chars
   *
   * @returns {Promise<{moved: string[], skipped: string[], failed: Array<{taskId, error}>}>}
   */
  const bulkRescheduleTasks = async (taskIds, change) => {
    if (!Array.isArray(taskIds) || taskIds.length === 0) {
      throw new Error('bulkRescheduleTasks: taskIds is required and must be non-empty');
    }
    if (!change || !(change.targetDate instanceof Date) || Number.isNaN(change.targetDate.getTime())) {
      throw new Error('bulkRescheduleTasks: change.targetDate must be a valid Date');
    }
    if (!change.reason || !VALID_RESCHEDULE_REASONS.has(change.reason)) {
      throw new Error(
        `bulkRescheduleTasks: reason must be one of ${[...VALID_RESCHEDULE_REASONS].join(', ')}`
      );
    }

    const target = change.targetDate;
    const moved   = [];
    const skipped = [];
    const failed  = [];

    // Sequential — keep Firestore write amplification predictable and let
    // each rescheduleTask compute against the latest in-memory snapshot.
    for (const taskId of taskIds) {
      const existing = tasks.find((t) => t.id === taskId);
      if (!existing) {
        failed.push({ taskId, error: 'not_found' });
        continue;
      }
      if (!existing.scheduledStart) {
        skipped.push(taskId);
        continue;
      }
      const prevStart = new Date(existing.scheduledStart);
      if (Number.isNaN(prevStart.getTime())) {
        failed.push({ taskId, error: 'invalid_existing_start' });
        continue;
      }
      // Preserve time-of-day on the destination day.
      const newStart = new Date(
        target.getFullYear(),
        target.getMonth(),
        target.getDate(),
        prevStart.getHours(),
        prevStart.getMinutes(),
        prevStart.getSeconds(),
        prevStart.getMilliseconds(),
      );
      // Preserve duration when an end time exists.
      let newEnd = null;
      if (existing.scheduledEnd) {
        const prevEnd = new Date(existing.scheduledEnd);
        if (!Number.isNaN(prevEnd.getTime())) {
          const durationMs = prevEnd.getTime() - prevStart.getTime();
          newEnd = new Date(newStart.getTime() + Math.max(0, durationMs));
        }
      }
      try {
        const result = await rescheduleTask(taskId, {
          newScheduledStart: newStart.toISOString(),
          newScheduledEnd:   newEnd ? newEnd.toISOString() : null,
          reason:            change.reason,
          note:              change.note || null,
        });
        if (result?.changed === false) {
          skipped.push(taskId);
        } else {
          moved.push(taskId);
        }
      } catch (err) {
        failed.push({ taskId, error: err?.message || String(err) });
      }
    }
    return { moved, skipped, failed };
  };

  // ── Rerender optimization ───────────────────────────────────────────────────
  // Action functions get permanently-stable identities via a ref indirection
  // (fresh closures each render, stable wrappers forever). The context value is
  // memoized so it only changes when data actually changes — a single task
  // status update no longer invalidates the value on unrelated provider renders.
  const actionsRef = React.useRef({});
  actionsRef.current = {
    ensureTaskWindow,
    ensureLeadTasks,
    addTask,
    updateTask,
    deleteTask,
    completeJob,
    getTasksForLead,
    deleteTasksForLead,
    addJob,
    addAdminTask,
    updateJob,
    updateAdminTask,
    rescheduleTask,
    bulkRescheduleTasks,
  };

  const stableActions = useMemo(() => {
    const names = [
      'ensureTaskWindow', 'ensureLeadTasks',
      'addTask', 'updateTask', 'deleteTask', 'completeJob',
      'getTasksForLead', 'deleteTasksForLead',
      'addJob', 'addAdminTask', 'updateJob', 'updateAdminTask',
      'rescheduleTask', 'bulkRescheduleTasks',
    ];
    return Object.fromEntries(
      names.map((n) => [n, (...args) => actionsRef.current[n](...args)])
    );
  }, []);

  const value = useMemo(() => ({
    // Raw subscription (kept for mixed surfaces + backward compat)
    tasks,
    loading,

    // Bounded window controls — expand to load historical/future ranges
    taskWindow,

    // Typed selectors — prefer these on surface code
    jobs,
    adminTasks,

    // Stable action identities (legacy + typed writers)
    ...stableActions,
  }), [tasks, loading, taskWindow, jobs, adminTasks, stableActions]);

  return <TasksContext.Provider value={value}>{children}</TasksContext.Provider>;
};

export default TasksContext;
